'use client';

import { Box, HStack, StackSeparator, VStack } from '@chakra-ui/react';
import UniversalHeader from '@/_components/Header'
import { useTranslations } from 'next-intl';
import { ORDER_TYPE_IDS } from '@/_constants';
import { resolveBrandId } from '@/_lib/brand-href';
import { orderServiceMika } from '@/_service/orders.service';
import { IRedux } from '@/_store/types';
import {
  PaginationItems,
  PaginationNextTrigger,
  PaginationPrevTrigger,
  PaginationRoot,
} from '@/components/ui/pagination';
import { useQuery } from '@tanstack/react-query';
import { useQueryState } from 'nuqs';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import DeliveryTypes from '../delivery-types';
import LoaderSkeleton from './loader-skeleton';
import Reservations from './reservations';
import { useAllReservationsList } from '@/_service/reservation';
import OrderList from './orders';

function Mobile() {
  const t = useTranslations();
  const { user } = useSelector((state: IRedux) => state.auth);

  const [orderType, setOrderType] = useQueryState('order_type', {
    defaultValue: 'delivery',
  });
  const [page, setPage] = useQueryState('page');
  const [selectedShipper] = useQueryState('shipper');

  // Build query params
  const queryParams = useMemo(() => {
    const params: Record<string, string | number | string[]> = {
      page: page || 1,
      limit: 20,
    };

    // Add order_type filter
    if (orderType) {
      params.order_type_ids = ORDER_TYPE_IDS[orderType];
    }

    // Add shipper filter
    if (selectedShipper) {
      params.shipper_id = resolveBrandId(selectedShipper);
    }

    return params;
  }, [page, orderType, selectedShipper]);

  const { data: ordersData, isLoading } = useQuery({
    queryKey: ['customer-orders', queryParams],
    queryFn: () => orderServiceMika.getList(user?.id || '', queryParams),
    enabled: Boolean(user),
    refetchInterval: 10000,
  });

  const { data: reservationsData, isLoading: isReservationsLoading } = useAllReservationsList({
    params: {
      page: page || 1,
      limit: 9,
    },
    props: {
      refetchInterval: 10000,
    }
  })

  const handleDeliveryTypeChange = (value: string) => {
    setPage('');
    setOrderType(value);
  }

  return (
    <>
      <VStack
        minH='100vh'
        separator={<StackSeparator borderColor={'background'} />}
        pb='120px'
        bg='#f5f4f3'
      >
        <Box w='100%'>
          <UniversalHeader backLink='/profile' title={t('my_orders')} />
        </Box>

        <Box w='100%' px='4' overflow='auto' scrollbar='hidden'>
          <DeliveryTypes handleDeliveryTypeChange={handleDeliveryTypeChange} />
        </Box>

        {isLoading ? (
          <LoaderSkeleton />
        ) : orderType === 'hall' ? <Reservations isLoading={isReservationsLoading} data={reservationsData} /> : <OrderList isLoading={isLoading} ordersData={ordersData} />}

        {Number(ordersData?.count) > 20 && (
          <PaginationRoot
            count={Number(ordersData?.count)}
            pageSize={20}
            defaultPage={1}
            page={Number(page) || 1}
            variant='subtle'
            mt={4}
            onPageChange={(e) => setPage(`${e.page}`)}
            colorScheme='red'
          >
            <HStack>
              <PaginationPrevTrigger color={'text.muted'} />
              <PaginationItems />
              <PaginationNextTrigger color={'text.muted'} />
            </HStack>
          </PaginationRoot>
        )}
      </VStack>
    </>
  );
}

export default Mobile;
